import { z } from 'zod';
import { FLOW_TRIGGER_MAX_QUERY_LIMIT } from './findAllOptions.js';
import { FlowTriggerKind } from './kind.js';
import { FlowTriggerVersionTarget } from '../flow/versionTarget.js';
/**
 * One trigger returned by `GET /triggers`. Each request is served from a single
 * source, dispatched by the `kind` filter — flow-service for every kind except
 * RESOURCE_EVENT, resource-gateway for RESOURCE_EVENT — never a merge of the two.
 * The fields present on an item depend on its `kind` and the requested
 * `payloadType`, so kind-specific fields are optional.
 */
export const FlowTriggerSchema = z.object({
    id: z.uuid().describe('Unique id of the trigger.'),
    flowId: z.uuid().describe('Id of the flow this trigger starts.'),
    organizationId: z.uuid().describe('Id of the organization that owns the trigger.'),
    kind: z
        .enum(FlowTriggerKind)
        .describe('What causes the trigger to fire, such as an HL7 message, voice call, or form event.'),
    created: z.iso.datetime().describe('When the trigger was created (ISO-8601 timestamp).'),
    updated: z.iso.datetime().describe('When the trigger was last updated (ISO-8601 timestamp).'),
    maxFlowRunsPerMinute: z
        .number()
        .nullish()
        .describe('Cap on flow runs this trigger may start per minute; null means no cap.'),
    versionTarget: z
        .enum(FlowTriggerVersionTarget)
        .nullish()
        .describe('Which flow version the trigger runs, such as Published or LatestDraft.'),
    desiredFlowRunPriority: z
        .number()
        .nullish()
        .describe('Execution priority for runs this trigger starts; lower numbers run first.'),
    messageType: z.string().nullish().describe('HL7 message type the trigger matches.'),
    messageTrigger: z.string().nullish().describe('HL7 trigger event the trigger matches.'),
    sendingFacility: z.string().nullish().describe('HL7 sending facility the trigger matches.'),
    phoneNumber: z.string().nullish().describe('Phone number a voice-call trigger listens on.'),
    assistantId: z.uuid().nullish().describe('Voice assistant that handles a matched call.'),
    fallbackDestination: z
        .string()
        .nullish()
        .describe('Number a voice-call trigger falls back to if the assistant cannot handle the call.'),
    interchangeSenderId: z
        .string()
        .nullish()
        .describe('X12 interchange sender id the trigger matches.'),
    transactionSetIdCode: z
        .string()
        .nullish()
        .describe('X12 transaction set id code the trigger matches.'),
    transactionSetPurposeCode: z
        .string()
        .nullish()
        .describe('X12 transaction set purpose code the trigger matches.'),
    fileType: z.string().nullish().describe('SFTP file type the trigger matches.'),
    jobKind: z.string().nullish().describe('Job kind whose completion fires the trigger.'),
    terminalStatus: z.string().nullish().describe('Terminal job status that fires the trigger.'),
    type: z
        .string()
        .nullish()
        .describe('Form event type, such as a submission, that fires the trigger.'),
    previsitFormTemplateId: z
        .uuid()
        .nullish()
        .describe('Single previsit form template a form-event trigger watches.'),
    previsitFormTemplateIds: z
        .array(z.uuid())
        .nullish()
        .describe('Previsit form templates a form-event trigger watches.'),
    resourceKey: z.string().nullish().describe('Resource key a resource-event trigger watches.'),
    state: z
        .enum(['Active', 'Inactive'])
        .nullish()
        .describe('Whether a resource-event trigger is currently active.'),
});
/** Response envelope for `GET /triggers`. */
export const FlowTriggersResponseSchema = z
    .object({
    triggers: z.array(FlowTriggerSchema).describe('Triggers on this page, matching the query.'),
    totalCount: z
        .number()
        .int()
        .nonnegative()
        .describe('Total triggers matching the query across all pages, ignoring limit and offset. ' +
        'Compare against offset + triggers.length to tell whether more pages remain.'),
    limit: z
        .number()
        .int()
        .positive()
        .max(FLOW_TRIGGER_MAX_QUERY_LIMIT)
        .describe('Page size applied to this response.'),
    offset: z
        .number()
        .int()
        .nonnegative()
        .describe('Number of matching triggers skipped before this page.'),
})
    .describe('A page of triggers matching the query, with paging metadata.');
//# sourceMappingURL=schema.js.map